import merge from 'lodash/merge';
import produce from 'immer';
import uuidv4 from 'uuid/v4';
import { ColorUtils } from '../../property';
import { SizeUtils } from '../../math';
import { createItemProps } from '../shared';
import { ItemClass } from '../shared/types';
import { TextAlign, TextDecorationTypes, TextSizing, TextUnit } from './types';
export function create(partial) {
    if (partial === void 0) { partial = {}; }
    var textDefaults = {
        id: uuidv4(),
        parentId: null,
        className: ItemClass.Text,
        size: SizeUtils.create(),
        sizing: [TextSizing.Auto, TextSizing.Auto],
        blocks: [],
        shadows: [],
        innerShadows: [],
        style: {
            fontSize: 16,
            fontSizeUnit: 'px',
            fontFamily: 'Lato',
            fontWeight: 400,
            lineHeight: 16,
            autoLineHeight: true,
            lineHeightUnit: TextUnit.Pixel,
            color: ColorUtils.create(),
            letterSpacing: 0,
            letterSpacingUnit: TextUnit.Pixel,
            align: TextAlign.Left,
            italic: false,
            underline: false,
            strikethrough: false,
        },
        itemProps: createItemProps(),
    };
    return merge(textDefaults, partial);
}
export function mSetContent(mutable, content) {
    var blocks = content.split('\n').map(function (t) { return ({
        text: t,
    }); });
    mutable.blocks = blocks;
    return mutable;
}
export var setContent = produce(mSetContent);
export function mSetSize(mutable, textSize) {
    mutable.size = textSize;
    return mutable;
}
export var setSize = produce(mSetSize);
export function mSetSizing(mutable, sizing) {
    mutable.sizing = sizing;
    return mutable;
}
export var setSizing = produce(mSetSizing);
export function mSetFontSize(mutable, fontSize, fontSizeUnit) {
    if (fontSizeUnit === void 0) { fontSizeUnit = 'px'; }
    mutable.style.fontSize = fontSize;
    mutable.style.fontSizeUnit = fontSizeUnit;
    return mutable;
}
export var setFontSize = produce(mSetFontSize);
export function mSetFontWeight(mutable, fontWeight) {
    mutable.style.fontWeight = fontWeight;
    return mutable;
}
export var setFontWeight = produce(mSetFontWeight);
export function mSetFontFamily(mutable, fontFamily) {
    mutable.style.fontFamily = fontFamily;
    return mutable;
}
export var setFontFamily = produce(mSetFontFamily);
export function mSetLineHeight(mutable, lineHeight) {
    mutable.style.lineHeight = lineHeight;
    return mutable;
}
export var setLineHeight = produce(mSetLineHeight);
export function mSetLineHeightUnit(mutable, unit) {
    mutable.style.lineHeightUnit = unit;
    return mutable;
}
export var setLineHeightUnit = produce(mSetLineHeightUnit);
export function mSetLetterSpacing(mutable, letterSpacing) {
    mutable.style.letterSpacing = letterSpacing;
    return mutable;
}
export var setLetterSpacing = produce(mSetLetterSpacing);
export function mSetLetterSpacingUnit(mutable, unit) {
    mutable.style.letterSpacingUnit = unit;
    return mutable;
}
export var setLetterSpacingUnit = produce(mSetLetterSpacingUnit);
export function getFontFamily(text) {
    return text.style.fontFamily;
}
export function getFontWeight(text) {
    return text.style.fontWeight;
}
export function getFontSize(text) {
    return text.style.fontSize;
}
export function getContent(text) {
    return text.blocks.map(function (block) { return block.text; }).join('\n');
}
export function getBlock(text, blockIdx) {
    var block = text.blocks[blockIdx];
    return (block === null || block === void 0 ? void 0 : block.text) || '';
}
export function getColor(text) {
    return text.style.color;
}
export function getSizing(text) {
    return text.sizing;
}
export function getSize(text) {
    return text.size;
}
export function getLineHeight(text, lineHeightUnit) {
    if (!lineHeightUnit)
        return text.style.lineHeight;
    return convertTextUnit(text.style.lineHeight, text.style.lineHeightUnit, lineHeightUnit, text.style.fontSize);
}
export function getLineHeightUnit(text) {
    return text.style.lineHeightUnit;
}
export function getLetterSpacing(text, letterSpacingUnit) {
    if (!letterSpacingUnit)
        return text.style.letterSpacing;
    return convertTextUnit(text.style.letterSpacing, text.style.letterSpacingUnit, letterSpacingUnit, text.style.fontSize);
}
export function getLetterSpacingUnit(text) {
    return text.style.letterSpacingUnit;
}
function getUnderlineDecoration(text) {
    return text.style.underline;
}
function getStrikethroughDecoration(text) {
    return text.style.strikethrough;
}
export function getTextDecoration(text) {
    if (getUnderlineDecoration(text)) {
        return TextDecorationTypes.Underline;
    }
    if (getStrikethroughDecoration(text)) {
        return TextDecorationTypes.Strikethrough;
    }
    return TextDecorationTypes.None;
}
export function mSetDecoration(mutable, value) {
    mutable.style.underline = value === TextDecorationTypes.Underline;
    mutable.style.strikethrough = value === TextDecorationTypes.Strikethrough;
    return mutable;
}
export var setDecoration = produce(mSetDecoration);
export function getAlign(text) {
    return text.style.align;
}
export function mSetAlign(mutable, align) {
    mutable.style.align = align;
    return mutable;
}
export var setAlignment = produce(mSetAlign);
function convertTextUnit(value, valueUnit, outputUnit, basePx) {
    if (valueUnit === outputUnit)
        return value;
    var valuePx;
    switch (valueUnit) {
        case TextUnit.Pixel:
            valuePx = value;
            break;
        case TextUnit.Percent:
            valuePx = basePx * (value / 100);
            break;
        default:
            throw new Error("Unsupported unit: " + valueUnit);
    }
    switch (outputUnit) {
        case TextUnit.Pixel:
            return valuePx;
        case TextUnit.Percent:
            return (valuePx / basePx) * 100;
        default:
            throw new Error("Unsupported unit: " + outputUnit);
    }
}
